import { useEffect, useState } from "react";
import {
  Bell,
  BellOff,
  BellRing,
  Mail,
  Smartphone,
  CheckCircle2,
  AlertTriangle,
  Send,
  Loader2,
} from "lucide-react";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import { isIosWithoutInstall } from "../../services/pushDevice";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  sendTestNotification,
} from "../../services/notificationPreferencesService";
import "./NotificationSettings.scss";

// Tipos de aviso que cada perfil vê na aba
const CATEGORIES = {
  freelancer: [
    {
      title: "Novas vagas",
      description: "Assim que uma vaga perto de você for publicada, com local, data e horário.",
      push: "newJobsPush",
      email: "newJobsEmail",
    },
    {
      title: "Minhas candidaturas",
      description: "Quando você for aprovado, receber um convite e 1 hora antes da vaga começar.",
      push: "applicationUpdatesPush",
      email: "applicationUpdatesEmail",
    },
    {
      title: "Mensagens do chat",
      description: "Quando uma empresa mandar mensagem e você estiver fora do app.",
      push: "chatPush",
    },
  ],
  company: [
    {
      title: "Novos candidatos",
      description: "Na hora em que alguém se candidatar às suas vagas, e um lembrete se o candidato ainda estiver sem resposta.",
      push: "candidatesPush",
      email: "candidatesEmail",
    },
    {
      title: "Mensagens do chat",
      description: "Quando um freelancer mandar mensagem e você estiver fora do app.",
      push: "chatPush",
    },
  ],
};

const INTRO = {
  freelancer: {
    title: "Não perca nenhuma oportunidade",
    text:
      "As melhores vagas costumam ser preenchidas nos primeiros minutos. Deixe as notificações ligadas " +
      "para saber na hora quando surgir uma vaga perto de você e quando uma empresa responder.",
  },
  company: {
    title: "Responda seus candidatos rápido",
    text:
      "Freelancers que ficam sem resposta acabam aceitando outras vagas. Deixe as notificações ligadas " +
      "para saber na hora quando alguém se candidatar e não perder bons profissionais.",
  },
};

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={`notif-toggle ${checked ? "notif-toggle--on" : ""}`}
      onClick={() => onChange(!checked)}
    >
      <span className="notif-toggle__thumb" />
    </button>
  );
}

export default function NotificationSettings({ userType = "freelancer" }) {
  const profile = userType === "company" ? "company" : "freelancer";
  const { isSupported, permission, isSubscribed, loading, requestPermission } = usePushNotifications();

  const [preferences, setPreferences] = useState(null);
  const [pushDevices, setPushDevices] = useState(0);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    getNotificationPreferences()
      .then((data) => {
        setPreferences(data.preferences);
        setPushDevices(data.pushDevices);
      })
      .catch(() => setFeedback({ type: "error", text: "Não foi possível carregar suas preferências." }));
  }, []);

  const updatePreference = async (key, value) => {
    const previous = preferences;
    const next = { ...preferences, [key]: value };
    setPreferences(next);
    setSaving(true);
    try {
      setPreferences(await updateNotificationPreferences(next));
    } catch {
      setPreferences(previous);
      setFeedback({ type: "error", text: "Não foi possível salvar. Tente novamente." });
    } finally {
      setSaving(false);
    }
  };

  const handleEnable = async () => {
    setFeedback(null);
    const ok = await requestPermission();
    if (ok) {
      setPushDevices((n) => Math.max(n, 1));
      setFeedback({ type: "success", text: "Notificações ativadas neste aparelho!" });
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setFeedback(null);
    try {
      const result = await sendTestNotification();
      setPushDevices(result.pushDevices);
      setFeedback(
        result.delivered
          ? { type: "success", text: "Notificação de teste enviada. Ela deve aparecer em alguns segundos." }
          : { type: "error", text: "Nenhum aparelho recebeu. Ative as notificações neste aparelho e tente de novo." }
      );
    } catch {
      setFeedback({ type: "error", text: "Não foi possível enviar o teste agora." });
    } finally {
      setTesting(false);
    }
  };

  const renderDeviceStatus = () => {
    if (!isSupported) {
      return isIosWithoutInstall() ? (
        <div className="notif-device notif-device--warn">
          <AlertTriangle size={20} />
          <div>
            <strong>Instale o app para receber notificações no iPhone</strong>
            <p>
              No Safari, toque em <b>Compartilhar</b> e depois em <b>Adicionar à Tela de Início</b>. Abra o app
              pelo ícone criado e volte nesta aba para ativar.
            </p>
          </div>
        </div>
      ) : (
        <div className="notif-device notif-device--warn">
          <AlertTriangle size={20} />
          <div>
            <strong>Este navegador não recebe notificações</strong>
            <p>Abra a plataforma no Chrome, Edge ou Safari (atualizados), ou instale o app no celular.</p>
          </div>
        </div>
      );
    }

    if (permission === "denied") {
      return (
        <div className="notif-device notif-device--error">
          <BellOff size={20} />
          <div>
            <strong>As notificações estão bloqueadas neste aparelho</strong>
            <p>
              Toque no cadeado ao lado do endereço do site (ou nas configurações do app) e mude
              <b> Notificações</b> para <b>Permitir</b>. Depois, recarregue a página.
            </p>
          </div>
        </div>
      );
    }

    if (permission === "granted" && isSubscribed) {
      return (
        <div className="notif-device notif-device--ok">
          <CheckCircle2 size={20} />
          <div>
            <strong>Notificações ativas neste aparelho</strong>
            <p>
              {pushDevices > 1
                ? `Você recebe avisos em ${pushDevices} aparelhos.`
                : "Você vai receber os avisos mesmo com o app fechado."}
            </p>
          </div>
          <button className="notif-btn notif-btn--ghost" onClick={handleTest} disabled={testing}>
            {testing ? <Loader2 size={16} className="notif-spin" /> : <Send size={16} />}
            Testar
          </button>
        </div>
      );
    }

    return (
      <div className="notif-device notif-device--off">
        <Bell size={20} />
        <div>
          <strong>Notificações desligadas neste aparelho</strong>
          <p>Ative para receber os avisos no celular, mesmo com o app fechado.</p>
        </div>
        <button className="notif-btn" onClick={handleEnable} disabled={loading}>
          {loading ? <Loader2 size={16} className="notif-spin" /> : <BellRing size={16} />}
          Ativar notificações
        </button>
      </div>
    );
  };

  return (
    <div className="notif-settings">
      <div className="notif-settings__intro">
        <BellRing size={28} />
        <div>
          <h2>{INTRO[profile].title}</h2>
          <p>{INTRO[profile].text}</p>
        </div>
      </div>

      <section className="notif-settings__section">
        <h3>
          <Smartphone size={18} /> Este aparelho
        </h3>
        {renderDeviceStatus()}
      </section>

      <section className="notif-settings__section">
        <h3>
          <Bell size={18} /> O que você quer receber
          {saving && <Loader2 size={14} className="notif-spin" />}
        </h3>

        {!preferences ? (
          <div className="notif-settings__loading">
            <Loader2 size={18} className="notif-spin" /> Carregando...
          </div>
        ) : (
          <div className="notif-settings__list">
            <div className="notif-settings__list-head">
              <span />
              <span>
                <Smartphone size={14} /> Celular
              </span>
              <span>
                <Mail size={14} /> E-mail
              </span>
            </div>

            {CATEGORIES[profile].map((category) => (
              <div className="notif-settings__row" key={category.title}>
                <div>
                  <strong>{category.title}</strong>
                  <p>{category.description}</p>
                </div>
                <span>
                  <Toggle
                    label={`${category.title} no celular`}
                    checked={preferences[category.push]}
                    onChange={(value) => updatePreference(category.push, value)}
                  />
                </span>
                <span>
                  {category.email ? (
                    <Toggle
                      label={`${category.title} por e-mail`}
                      checked={preferences[category.email]}
                      onChange={(value) => updatePreference(category.email, value)}
                    />
                  ) : (
                    <span className="notif-settings__na">—</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        <p className="notif-settings__hint">
          Os avisos dentro da plataforma (o sininho) continuam sempre ligados.
        </p>
      </section>

      {feedback && (
        <div className={`notif-feedback notif-feedback--${feedback.type}`} role="status">
          {feedback.text}
        </div>
      )}
    </div>
  );
}
